import React from 'react';
import { useEditor, useNode } from '@craftjs/core';

/**
 * Posición y tamaño de un elemento dentro de una `Section` en modo libre.
 *
 * `x` y `w` van en PORCENTAJE del ancho del área; `y` y `h` en PÍXELES. El
 * porcentaje horizontal hace que la posición y el ancho aguanten cualquier
 * pantalla sin recalcular nada; lo vertical en píxeles porque el alto del área
 * no es proporcional al ancho y un porcentaje ahí se movería solo al crecer el
 * contenido.
 *
 * `w` y `h` son OPCIONALES: lo guardado como `{ x, y }` antes de que existiera
 * el redimensionado sigue siendo válido y se lee como "tamaño automático".
 */
export interface PosicionLibre {
  x: number;
  y: number;
  w?: number;
  h?: number;
  /**
   * Donde queda el contenido DENTRO del alto del elemento: arriba, al medio o
   * abajo. Ausente = arriba, que es como se comportaba antes de que existiera.
   *
   * Va en `_layout` y no como prop de cada componente a proposito: es geometria,
   * la entienden los dos repos con un solo cambio y sirve para cualquier
   * componente sin tocar su codigo.
   */
  v?: AlineacionVertical;
  /**
   * El ultimo alto que el dealer eligio A MANO, en px.
   *
   * `h` es el alto con el que se dibuja y puede haber crecido solo para que
   * entre el texto; `hMin` es hasta donde puede volver cuando ese texto se
   * borra. Sin el, o el frame se quedaba con todo el alto que gano —aunque
   * quedara vacio— o al achicarse se comia el alto que la persona habia elegido.
   *
   * Lo escribe unicamente un gesto de redimensionado (y la suelta inicial, que
   * es el primer tamano que se vio). El crecimiento automatico no lo toca nunca:
   * ahi esta la diferencia entre "lo eligio alguien" y "lo pidio el contenido".
   *
   * El sitio publicado lo ignora: le alcanza con `h`.
   */
  hMin?: number;
  /**
   * Coordenadas propias para móvil. Ausente = el elemento se apila, que es lo
   * que hace hoy TODO lo que ya está guardado.
   *
   * Nace recién cuando alguien mueve o redimensiona el elemento estando en la
   * vista móvil del editor: hasta entonces no existe y no se escribe. Por eso
   * una página vieja renderiza exactamente igual —mismas clases, mismo HTML— y
   * por eso editar en móvil no puede tocar la posición de escritorio: son dos
   * campos distintos del mismo `_layout`.
   *
   * Tablet (768px) queda ABAJO del corte, que ahora es `xl`: ahí manda `m`.
   */
  m?: PosicionMovil;
}

/**
 * La posición en móvil. Mismo significado que arriba —`x` en %, `y`/`h` en px—
 * pero medida sobre el ancho del canvas móvil, no sobre el de escritorio.
 *
 * No lleva `hMin`: el piso del alto manual es una sola decisión por elemento y
 * vive en el nivel de arriba.
 */
export interface PosicionMovil {
  x: number;
  y: number;
  w?: number;
  h?: number;
  v?: AlineacionVertical;
}

export type AlineacionVertical = 'start' | 'center' | 'end';

const VERTICALES = new Set(['start', 'center', 'end']);

/** Marca el área dentro de la cual se posicionan los hijos libres. */
export const ATRIBUTO_AREA_LIBRE = 'data-area-libre';

/**
 * Capa de elementos agregados sobre una sección existente.
 *
 * Las secciones de primer nivel —Hero, FAQ, CTA, galería…— no son canvas de
 * Craft: no reciben hijos, y arrastrarles algo encima terminaba dejándolo como
 * hermano en ROOT, arriba de la sección. En vez de convertir 66 componentes en
 * canvas (y tocar su render en los dos repos), cada sección recibe una **capa
 * enlazada**: un `<Element id="capaLibre" canvas>` que `RenderNodoLibre` cuelga
 * desde `onRender`, sin que el componente de la sección se entere.
 *
 * Es un **nodo enlazado**, no un hijo: vive en `linkedNodes.capaLibre` del nodo
 * de la sección, así que
 *
 * - las páginas viejas no tienen la clave y no cambian en nada;
 * - mover o reordenar la sección se lleva sus elementos agregados;
 * - el JSON existente no se reestructura: solo aparece una clave nueva cuando
 *   alguien efectivamente suelta algo.
 */
export const ID_CAPA_LIBRE = 'capaLibre';

/** Atributo del envoltorio de la sección; lleva el id del nodo de su capa. */
export const ATRIBUTO_CAPA = 'data-capa-libre';

/**
 * La capa tapa toda la sección pero no le roba el mouse: solo sus hijos reciben
 * eventos (ver `EnvoltorioLibre`). Sin esto no se podría seguir editando el
 * contenido original de la sección.
 *
 * ## Por qué lleva `z-index`
 *
 * Casi todas las secciones envuelven su contenido en un `relative z-10` (20 lo
 * hacen). La capa va después en el DOM, pero sin `z-index` propio pierde contra
 * ese `z-10`: el elemento agregado quedaba PINTADO ATRÁS y, peor, el contenido
 * de la sección se llevaba el puntero — se soltaba un Texto sobre un Hero, se
 * veía, y después no había forma de agarrarlo porque el `h1` invisible de al
 * lado recibía el `pointerdown`. Con `50` gana a todo lo que usan las secciones
 * (el máximo es `z-40`) y sigue perdiendo contra la barra de navegación, que se
 * pinta en 100. La capa no roba nada: sigue con `pointer-events: none`, y lo
 * único que pasa al frente son los elementos que el dealer agregó encima, que es
 * justamente donde tienen que estar.
 */
export const ESTILO_CAPA: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  zIndex: 50,
};

/**
 * Componentes que YA reciben hijos por su cuenta y no llevan capa: `Section` es
 * el lienzo en blanco y `Container`/`Grid` son cajas que el dealer arma a mano.
 */
export const CON_HIJOS_PROPIOS = new Set(['Section', 'Container', 'Grid']);

/**
 * Alto manual de una sección de primer nivel, en px. Ausente = el de siempre.
 *
 * ## Por qué hace falta un prop nuevo
 *
 * `Section` declara `minHeight` y su render lo aplica, así que la barra podía
 * moverle el alto desde el primer día. Las otras sesenta y pico de secciones no:
 * cinco declaran un alto propio (`height`, `heroHeight`) y el resto se mide por
 * su contenido. Inventarles un `minHeight` sería agregarles un prop que su render
 * ignora — el dealer vería un control que no hace nada.
 *
 * Este es el contrato común para esas: no lo aplica la sección, lo aplica el
 * envoltorio que `RenderNodoLibre` ya le cuelga a toda sección raíz. Por eso
 * funciona en las sesenta sin tocar ni una.
 *
 * ## Qué ve una página que nunca lo usó
 *
 * Exactamente lo de antes. Sin `_alto` el envoltorio conserva su estilo tal cual
 * y no se emite una sola declaración distinta. Solo se escribe cuando alguien
 * mueve el alto a mano, así que nada se migra solo.
 */
export const PROP_ALTO_SECCION = '_alto';

/**
 * Alto manual de la sección **abajo del corte**, en px. Ausente = el de siempre.
 *
 * Es el hermano de `_layout.m` para la sección: un campo aparte, que el Builder
 * escribe recién cuando alguien arrastra la manija en una vista responsive, y
 * que no se deriva ni se copia nunca del alto de escritorio.
 *
 * Hace falta porque `--seccion-min-h` se aplica con `lg:min-h-[...]`, o sea sólo
 * arriba del corte. Reutilizar ese mismo valor abajo tampoco servía: es un alto
 * elegido mirando un canvas de 1280 y aplicado tal cual a 390 reserva cientos de
 * píxeles en blanco.
 *
 * Sin este prop no se emite ninguna declaración nueva: nada se migra.
 *
 * Tablet comparte este alto con móvil, igual que comparte `_layout.m`.
 *
 * MANTENER EN SYNC con goautos-admin.
 */
export const PROP_ALTO_SECCION_MOVIL = '_altoMovil';

/** El alto manual si es un número usable; `0` —o sea, "no hay"— en cualquier otro caso. */
export const altoDeSeccion = (valor: unknown): number => {
  const n = Number(valor);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * Estilo del envoltorio de una sección raíz.
 *
 * Sin alto manual es el `position: relative` de siempre, carácter por carácter.
 *
 * Con alto manual pasa a `grid` de una sola fila: un ítem de grid se estira en
 * los dos ejes por defecto, así que la sección crece hasta el alto pedido y su
 * fondo cubre todo. Con `minHeight` a secas el envoltorio crecía pero la sección
 * se quedaba de su tamaño y abajo asomaba el fondo de la página. Es `minHeight`
 * y no `height` a propósito: si el contenido necesita más, sigue creciendo — el
 * alto manual es un piso, nunca un recorte.
 *
 * Los demás hijos del envoltorio —la barra, el "+ Añadir sección", la capa— son
 * todos `absolute`, así que la fila `1fr` es de la sección y de nadie más.
 */
export const estiloDeSeccion = (alto: number, altoMovil = 0): React.CSSProperties => ({
  ...(alto
    ? { position: 'relative' as const, minHeight: alto, display: 'grid', gridTemplateRows: '1fr' }
    : { position: 'relative' as const }),
  // El alto responsive no puede ir en el `minHeight` de arriba: un estilo en
  // línea no tiene media query y le ganaría al de escritorio en TODOS los
  // anchos. Viaja como variable y la aplica `CLASE_ALTO_SECCION_MOVIL`, que sí
  // está acotada a `max-lg`.
  ...(altoMovil ? { ['--seccion-alto-movil' as string]: `${altoMovil}px` } : {}),
  // Con alto responsive pero sin alto de escritorio, el envoltorio igual tiene
  // que ser grid para que la sección se estire hasta el piso pedido.
  ...(altoMovil && !alto ? { display: 'grid', gridTemplateRows: '1fr' } : {}),
});

/**
 * La clase que aplica el alto responsive de una sección raíz.
 *
 * Va en CLASE y no en el `style` porque un estilo en línea no admite media
 * query: ahí adentro el corte no existiría y el alto responsive pisaría también
 * a escritorio. Y va completa, sin interpolar, porque Tailwind purga por
 * coincidencia de texto.
 *
 * Es `min-height` y no `height`: el alto elegido es un PISO. Si el contenido
 * necesita más, la sección crece — por eso achicar no corta ni superpone nada
 * que esté en el flujo.
 *
 * MANTENER EN SYNC con goautos-admin.
 */
export const CLASE_ALTO_SECCION_MOVIL = 'max-lg:min-h-[var(--seccion-alto-movil)]';

/** Prop donde cada hijo guarda su posición. Ausente = sigue en el flujo normal. */
export const PROP_POSICION = '_layout';

/**
 * Qué se puede mover y redimensionar dentro de una sección libre.
 *
 * Están todos los componentes de la pestaña "Componentes" del Toolbox MENOS los
 * canvas estructurales (`Section`, `Container`, `Grid`): ésos reciben hijos y
 * tienen su propia lógica de soltado, y sacarlos del flujo mezclaría dos
 * sistemas de layout sobre el mismo árbol. Se tratan aparte.
 */
export const POLITICA_RESIZE = {
  // Texto y titulo con alto propio: es lo que permite alinear el contenido
  // dentro del frame (arriba, al medio, abajo) como en Hostinger. Sin alto no
  // hay sobrante que repartir. El alto sigue siendo opcional: lo que ya estaba
  // guardado sin `h` se sigue comportando igual.
  Text: 'ambos',
  Heading: 'ambos',
  Button: 'ancho',
  Icon: 'ambos',
  Divider: 'ancho',
  Spacer: 'ambos',
  SocialLinks: 'ancho',
  ImageText: 'ancho',
  Card: 'ambos',
  ListBlock: 'ancho',
  GalleryBlock: 'ancho',
  AccordionBlock: 'ancho',
  IconText: 'ancho',
  // Ocho manijas y no dos: no es una linea de texto como `Button`, es un bloque
  // de dos partes —el boton y, cuando falta el numero, su aviso abajo— apilado en
  // columna. Tiene alto propio que repartir, asi que se agarra de las esquinas
  // como cualquier caja.
  WhatsAppButton: 'ambos',
  // La imagen es un FRAME: el dealer le da el ancho y el alto que quiera y la
  // foto lo llena recortándose (`object-fit: cover`), como en Hostinger. Forzar
  // la proporción dejaba elegir una sola medida y no permitía un recorte
  // panorámico o cuadrado. El video sí la mantiene: recortarlo pierde imagen.
  Image: 'ambos',
  // El carrusel es un frame igual que la imagen: el dealer elige la caja y las
  // diapositivas la llenan.
  Carousel: 'ambos',
  VideoPlayer: 'proporcional',
  MapEmbed: 'ambos',
} as const;

/**
 * Los que son un MARCO: la caja se elige y el contenido la llena recortándose.
 *
 * Es la diferencia con todo lo demás. Una foto, un video, un carrusel o un mapa
 * se recortan a la caja a propósito —así se puede pedir un recorte panorámico o
 * cuadrado— y por eso su caja puede ser más chica que su contenido. En cambio un
 * texto o un botón no se recortan: achicarlos abajo de lo que ocupan no los hace
 * más chicos, los desborda, y la caja azul deja de ser lo que se ve.
 *
 * Lista de nombres, sí, pero de CAPACIDAD: contesta "¿esto recorta?", igual que
 * `POLITICA_RESIZE` contesta "¿por dónde se agarra?".
 */
const MARCOS = new Set(['Image', 'Carousel', 'VideoPlayer', 'MapEmbed']);

/**
 * ¿Este componente es un marco?
 *
 * Importa fuera del resize por una razón concreta: un marco no tiene alto
 * propio. `Image`, `Carousel` y `VideoPlayer` piden `height: 100%` cuando están
 * en la capa, o sea que su alto ES el del envoltorio. Los demás se miden por su
 * contenido y no dependen de nadie.
 */
export const esMarco = (nombre: string | undefined): boolean =>
  !!nombre && MARCOS.has(nombre);

/**
 * Los marcos que YA traen su propia proporción: hoy sólo `Image`.
 *
 * Un `<img>` es un elemento reemplazado — el navegador conoce su tamaño real y
 * puede medirse solo. `Carousel`, `VideoPlayer` y `MapEmbed` no: son `div`s con
 * hijos absolutos o un `iframe`, y sin una caja que les diga el alto colapsan a
 * cero (ticket #69). De ahí que abajo de `lg` se los trate distinto.
 *
 * ## Qué cambia para la imagen
 *
 * Abajo del corte el elemento vuelve al flujo y ocupa TODO el ancho de la
 * sección, pero conservaba el alto de una caja diseñada para otro ancho. La
 * proporción de esa caja no es la de la foto, así que `object-fit: cover'
 * recortaba — y estirada a todo el ancho, ese recorte se vuelve enorme. Medido
 * en los logos de B2 Autos a 490px: KYC se dibujaba a 458x150 desde un original
 * de 158x48, o sea 2.9x de escala y 7% de los costados cortado; ZX Auto perdía
 * el 11%.
 *
 * Con su proporción propia la imagen se ve ENTERA: sin recorte, sin deformar y
 * sin franjas. Arriba de `xl` no cambia nada — ahí manda la caja que el dealer
 * dibujó.
 */
export const tieneAspectoPropio = (nombre: string | undefined): boolean =>
  nombre === 'Image' || nombre === 'Carousel';

/** Si el resize tiene que frenar contra el contenido real del elemento. */
export const respetaSuContenido = (nombre: string | undefined): boolean =>
  !!nombre && esMovible(nombre) && !MARCOS.has(nombre);

export type PoliticaResize = (typeof POLITICA_RESIZE)[keyof typeof POLITICA_RESIZE];

export const esMovible = (nombre: string | undefined): boolean =>
  !!nombre && nombre in POLITICA_RESIZE;

export const politicaDe = (nombre: string | undefined): PoliticaResize =>
  (POLITICA_RESIZE as Record<string, PoliticaResize>)[nombre ?? ''] ?? 'ancho';

const numeroOpcional = (valor: unknown): number | undefined => {
  const n = Number(valor);
  return Number.isFinite(n) && n > 0 ? n : undefined;
};

/** Normaliza y acota al área: 0–100% en x, nunca negativo en y. */
export const normalizarPosicion = (valor: unknown): PosicionLibre | null => {
  if (!valor || typeof valor !== 'object') return null;
  const p = valor as Record<string, unknown>;
  if (!Number.isFinite(Number(p.x)) || !Number.isFinite(Number(p.y))) return null;
  return {
    x: Math.max(0, Math.min(100, Number(p.x))),
    y: Math.max(0, Number(p.y)),
    w: numeroOpcional(p.w),
    h: numeroOpcional(p.h),
    hMin: numeroOpcional(p.hMin),
    ...(normalizarMovil(p.m) ? { m: normalizarMovil(p.m)! } : {}),
    ...(VERTICALES.has(String(p.v)) ? { v: p.v as AlineacionVertical } : {}),
  };
};

/**
 * Lo mismo para la posición móvil. Devuelve `null` si no hay una válida, y ese
 * `null` es el que mantiene intacto todo lo guardado: sin `m` no se emite ni
 * una clase distinta.
 */
export const normalizarMovil = (valor: unknown): PosicionMovil | null => {
  if (!valor || typeof valor !== 'object') return null;
  const p = valor as Record<string, unknown>;
  if (!Number.isFinite(Number(p.x)) || !Number.isFinite(Number(p.y))) return null;
  return {
    x: Math.max(0, Math.min(100, Number(p.x))),
    y: Math.max(0, Number(p.y)),
    w: numeroOpcional(p.w),
    h: numeroOpcional(p.h),
    ...(VERTICALES.has(String(p.v)) ? { v: p.v as AlineacionVertical } : {}),
  };
};

/**
 * Clases del elemento posicionado.
 *
 * El posicionamiento absoluto arranca recién en `lg` (1024px): abajo de eso el
 * elemento se queda en el flujo normal y los hijos se apilan. Es a propósito —
 * una coordenada pensada para 1280px de ancho, aplicada tal cual en 390px,
 * termina con elementos encimados o fuera de la pantalla. Cuando haga falta un
 * responsive de verdad, se guardarán coordenadas por breakpoint sin cambiar nada
 * de lo que ya está guardado.
 *
 * El corte está en 1024 y no en 768 porque `x`/`w` se guardan en % del ancho y
 * `y`/`h` en px: al angostar, la caja se achica en X, el texto no escala y
 * necesita más alto, pero el alto reservado quedó congelado en píxeles. Medido
 * con `npm run test:sweep-responsive`: el contenido se salía de su sección
 * desde 1021px y se superponía desde 1154px. 1280 sigue siendo el ancho contra el
 * que se congelaron las coordenadas ya guardadas, así que arriba del corte no
 * cambia un píxel.
 *
 * Están escritas COMPLETAS y se eligen con ternarios: Tailwind purga por
 * coincidencia de texto y no ve las clases que se arman interpolando. Por eso
 * las medidas viajan en variables CSS.
 *
 * `position` y las medidas van en CLASES y no en el `style`: un estilo en línea
 * le gana a cualquier clase, así que ahí adentro `lg:absolute` no se aplicaría
 * nunca.
 */
const BASE = 'relative lg:absolute lg:left-[var(--libre-x)] lg:top-[var(--libre-y)] lg:max-w-[calc(100%_-_var(--libre-x))]';
const ANCHO_AUTO = 'lg:w-fit';
const ANCHO_FIJO = 'lg:w-[var(--libre-w)]';
const ALTO_FIJO = 'lg:h-[var(--libre-h)]';

/**
 * Ancho con el que se eligió el alto, en px.
 *
 * El editor dibuja la página dentro de un canvas de 1280 —es el `maxWidth` por
 * defecto de `Section` y el ancho que asumen las coordenadas libres—, así que
 * cuando alguien arrastra la manija hasta "530px de alto" lo está haciendo
 * sobre una caja de ese ancho. Es el dato que falta para saber qué PROPORCIÓN
 * quiso: 530px no significa nada sin el ancho contra el que se eligió.
 *
 * No es una medida del sitio publicado —ahí una sección a todo lo ancho puede
 * dar 1900px— sino del lugar donde se tomó la decisión.
 */
const ANCHO_DE_REFERENCIA = 1280;

/**
 * Abajo de `xl` el marco conserva su PROPORCIÓN, no su alto en píxeles.
 *
 * El elemento vuelve al flujo y no lleva medidas: el alto era `lg:h-[...]` y
 * ahí no existe. Para un texto está bien —se mide por su contenido— pero un
 * marco no tiene contenido que lo mida: `Carousel`, `Image` y `VideoPlayer`
 * piden `height: 100%`, ese 100% se resuelve contra un padre de alto automático
 * y da CERO. La sección, en cambio, conserva su `min-height`, y lo que queda es
 * un hueco en blanco con el elemento colapsado adentro.
 *
 * Arrastrar el alto de escritorio tal cual tampoco sirve: 530px elegidos sobre
 * una caja de 1280 son una franja panorámica, y los mismos 530px sobre 343 de
 * ancho son un rectángulo vertical. La imagen entra igual —`object-fit` la
 * acomoda— pero queda una moldura enorme arriba y abajo. Lo que se conserva
 * entonces es la relación de aspecto, que es lo que la persona realmente
 * eligió, y el alto sale del ancho que haya.
 *
 * `lg:aspect-auto` para que arriba del corte vuelva a mandar `lg:h-[...]`.
 *
 * Vale para TODO lo que hay abajo del corte, no sólo para móvil: entre 768 y
 * 1280 el marco también vuelve al flujo y ahí `lg:h-[...]` no aplica. Dejar la
 * proporción cortada en `md` lo devolvería al colapso a cero. Y el recorte no
 * se pierde: la proporción que se emite es la que se eligió sobre el canvas de
 * 1280, mientras que el alto en px congelado, aplicado a una caja más angosta,
 * ya venía deformándolo de a poco.
 *
 * Sólo para marcos, a propósito: son los que se recortan por diseño. Fijarle
 * una proporción a un texto sería peor — en móvil ocupa más líneas y se
 * desbordaría.
 */
const ASPECTO_MARCO = 'aspect-[var(--libre-ar)] lg:aspect-auto';

/**
 * Cuánto ocupa de la FILA cada elemento abajo de `lg`.
 *
 * Abajo del corte los hijos vuelven al flujo, y hasta ahora se apilaban uno por
 * uno a todo el ancho. Para un texto está bien; para una tira de logos es lo que
 * la rompía: cuatro piezas pensadas para ocupar un 17-24% cada una se dibujaban
 * a 358px de ancho, una debajo de la otra, tres veces más grandes que su
 * original y ocupando media pantalla.
 *
 * El sitio de referencia (b2autos.cl) los pone en una FILA QUE ENVUELVE, todos a
 * la misma caja: medido a 390px, 188x58 cada uno, dos por fila.
 *
 * La regla que da eso sin inventar tamaños: cada marco pide el ancho con el que
 * se diseñó, con un piso de media fila. Un marco angosto (los logos, 14-24%)
 * sube al piso y entran dos por fila; uno ancho (el banner, 92.5%) se queda con
 * el suyo y ocupa la fila entera. Es una sola expresión, sin breakpoints
 * intermedios, así que al arrastrar el ancho no hay ningún salto.
 *
 * Lo que NO es marco sigue tomando la fila completa: un texto repartido en media
 * fila se desborda en cuanto necesita una línea más. O sea, para texto y botones
 * el resultado es exactamente el de siempre.
 */
/**
 * El piso es en PÍXELES, no en porcentaje.
 *
 * Con un piso de media fila el logo crecía con la pantalla: a 1024px pedía 490px
 * —el doble que en el escritorio— y quedaba enorme justo antes del corte. Con un
 * piso fijo el ancho diseñado lo alcanza y lo pasa solo: 16.75% son 58px a 390,
 * 123 a 768, 166 a 1024 y 209 a 1279, así que la tira converge sola hacia la
 * distribución de escritorio y el cruce de 1280 casi no se nota.
 *
 * El piso son 130px y no más: los logos de B2 Autos son recortes AJUSTADOS a la
 * marca (Riddara 414x129, Dongfeng 1006x150), mientras que los del sitio de
 * referencia son piezas normalizadas de 390x121 con aire alrededor. A la misma
 * caja, los nuestros se ven bastante más grandes. 130px deja la marca del tamaño
 * al que se lee en la referencia y mantiene dos por fila.
 *
 * El `min(...,45%)` es para los teléfonos angostos: a 320px, un piso fijo dejaría
 * un solo logo por fila. Con el 45% siguen entrando dos a cualquier ancho.
 */
/**
 * En el teléfono, DOS POR FILA exactas.
 *
 * Con una caja fija de 130px el reparto salía 2+2 hasta 440px, 3+1 entre 450 y
 * 590, y 4 en fila desde 600: entre medio entran tres cajas pero no cuatro. Y no
 * hay un ancho de caja ni un gap que evite esa ventana — si tres entran y cuatro
 * no, el reparto es 3+1. La cuenta: 3 entran cuando `3*130 + 2*gap <= ancho` y 4
 * cuando `4*130 + 3*gap <= ancho`; la segunda es siempre más exigente, así que
 * la ventana existe para cualquier gap fijo. Hace falta un corte.
 *
 * Abajo de `sm` (640px) cada logo reserva MEDIA FILA —de ahí las dos por fila— y
 * la pieza de adentro se queda en sus 130px, centrada en su celda. O sea: cambia
 * el reparto, no el tamaño. A 639px son 2+2 y a 640px son 4 en fila, sin pasar
 * nunca por 3+1 y sin que la caja cambie de tamaño en el cruce.
 */
const BASE_FILA_MARCO = [
  'max-sm:basis-[calc(50%_-_6px)]',
  'max-sm:[&>*]:max-w-[130px]',
  'max-sm:[&>*]:mx-auto',
  'sm:basis-[max(var(--libre-w),min(130px,45%))]',
  'lg:basis-auto',
].join(' ');

/** Fila completa: lo que ya hacía todo lo que no es un marco. */
const BASE_FILA_PLENA = 'basis-full lg:basis-auto';

/**
 * El banner: un marco que ocupa casi toda la fila aprovecha TODO el ancho
 * disponible en el teléfono.
 *
 * Su ancho de diseño es un porcentaje del canvas de 1280 —92.533% en la home de
 * B2 Autos— y aplicado tal cual a 390px deja 331px de banner en una caja de 358:
 * 27px de aire que en un teléfono se notan. El sitio de referencia lo dibuja a
 * todo el ancho.
 *
 * `max(ancho de diseño, min(100%, 640px))` da exactamente eso sin ningún salto:
 *
 * - hasta ~690px de viewport el piso es el 100% y el banner ocupa toda la caja;
 * - entre ~690 y ~724 el piso se queda en 640px fijos mientras el ancho de
 *   diseño lo alcanza;
 * - de ahí para arriba manda el ancho de diseño, igual que siempre.
 *
 * En el punto de relevo los dos valen 640px, así que al arrastrar el ancho la
 * medida crece de forma continua. Arriba de `xl` esto no existe: ahí el
 * elemento es absoluto y manda su caja.
 */
/**
 * Y en el teléfono sale a SANGRE COMPLETA: se come el gutter de la sección.
 *
 * La sección reserva `paddingX` a cada lado —16px en la home de B2 Autos— y el
 * banner quedaba adentro: 358px de banner en una pantalla de 390. A sangre da
 * 390x199 en vez de 358x183, un 9% más, y es como se ve un banner en cualquier
 * sitio hecho para teléfono.
 *
 * La sangría NO se enciende con un breakpoint: se derrite sola.
 * `clamp(0px, (690px - 100vw)/10, var(--seccion-px))` vale el gutter entero
 * hasta 530px de viewport, y de ahí baja linealmente hasta cero a los 690 —
 * justo donde el ancho de diseño empieza a mandar. Así no hay ningún escalón en
 * todo el recorrido, y arriba de 690px la sangría es 0 y las clases de sangría
 * no cambian nada aunque estén.
 *
 * El ancho suma la sangría de los dos lados y los márgenes negativos la
 * devuelven, así que el tamaño exterior del ítem sigue siendo exactamente el de
 * la caja de contenido: el flex no lo encoge y no aparece scroll horizontal.
 *
 * Los márgenes van con `max-xl:` para que arriba del corte no existan: ahí el
 * elemento recupera su `xl:ml-[var(--libre-x)]` sin pelearse con nadie.
 */
const BASE_FILA_ANCHA = [
  '[--libre-sangria:clamp(0px,calc((690px_-_100vw)/10),var(--seccion-px,0px))]',
  'basis-[max(var(--libre-w),min(calc(100%_+_2_*_var(--libre-sangria)),640px))]',
  'max-xl:ml-[calc(var(--libre-sangria)_*_-1)]',
  'max-xl:mr-[calc(var(--libre-sangria)_*_-1)]',
  'lg:basis-auto',
].join(' ');

/**
 * Desde qué ancho de diseño un marco cuenta como 'banner'.
 *
 * Por debajo de esto es una pieza que convive con otras en la fila (los logos
 * son 14-24%) y estirarla a todo el ancho rompería el reparto.
 */
const ANCHO_DE_BANNER = 80;

/** La fila que le toca a este hijo abajo de `lg`. */
const filaDe = (pos: PosicionLibre, marco: boolean): string => {
  if (!marco || !pos.w) return BASE_FILA_PLENA;
  return pos.w >= ANCHO_DE_BANNER ? BASE_FILA_ANCHA : BASE_FILA_MARCO;
};

/**
 * El respaldo cuando no se puede calcular la proporción: sin `w` guardado no
 * hay ancho con el que comparar el alto. Es peor que la proporción, pero
 * infinitamente mejor que el colapso a cero.
 */
const ALTO_FIJO_MARCO = 'h-[var(--libre-h)]';

/**
 * Arriba de `xl`, un marco que está SOLO en su sección libre se queda en el
 * FLUJO y conserva su proporción, en vez de irse a `absolute` con el alto
 * congelado en píxeles.
 *
 * ## Qué arregla
 *
 * `w` va en % del ancho y `h` en px, así que arriba del corte la caja se
 * ensancha con la pantalla mientras el alto no se mueve: la proporción crece
 * sola y, con `object-fit: cover`, el recorte con ella. Medido en el banner de
 * B2 Autos: la caja pasa de 1.76 a 1280px —la proporción de la foto, que ahí se
 * ve entera, y es el ancho del canvas del editor— a 2.64 a 1920px (26% de la
 * foto recortado) y a 3.30 a 2400px (41%). Es el mismo problema que `ASPECTO_
 * MARCO` ya resuelve ABAJO del corte, con la misma solución.
 *
 * ## Por qué en el flujo y no `absolute` con `aspect-ratio`
 *
 * Un hijo absoluto no le suma alto a la sección, y el alto de la sección es un
 * dato guardado (`minHeight`) que alguien eligió mirando el canvas de 1280. Si
 * el marco crece quedándose absoluto, crece POR ENCIMA de una sección que no se
 * entera: medido a 1920px, el banner pasaba a 1011px y se metía 324px adentro de
 * la sección siguiente. En el flujo la sección se mide por él y crece sola; el
 * `minHeight` guardado sigue siendo el piso.
 *
 * ## Por qué SÓLO si está solo en su sección
 *
 * Un marco que crece empuja lo que tiene debajo. Mientras es el único hijo no
 * hay nada a lo que empujar y el cambio no puede romper ninguna página ya
 * publicada. Con hermanos sí: de los 13 marcos con alto congelado que hay hoy en
 * las configuraciones guardadas, dos tienen un elemento justo abajo. Ésos se
 * quedan como están hasta que se decida qué hacer con ellos.
 *
 * ## Por qué no está en goautos-admin
 *
 * El canvas del editor mide `ANCHO_DE_REFERENCIA`, que es justo el ancho donde
 * las dos formas coinciden: no hay nada que arreglar ahí. Y el editor posiciona
 * los controles y calcula los gestos sobre un elemento `absolute`; sacarlo del
 * flujo le movería el origen a las cuentas del arrastre.
 */
const FLUJO_MARCO = 'relative aspect-[var(--libre-ar)] lg:ml-[var(--libre-x)] lg:mt-[var(--libre-y)] lg:w-[var(--libre-w)]';

/**
 * Lo mismo para un marco con proporción propia: abajo de `lg` se mide solo, y
 * la caja de diseño vuelve a mandar recién arriba del corte.
 */
const FLUJO_MARCO_PROPIO = 'relative lg:aspect-[var(--libre-ar)] lg:ml-[var(--libre-x)] lg:mt-[var(--libre-y)] lg:w-[var(--libre-w)]';

/**
 * El marco que se mide solo Y está solo en su sección: NINGUNA proporción, en
 * ningún ancho. La caja es el contenido.
 *
 * Es lo que hace el sitio de referencia (b2autos.cl) con su banner: medido de
 * 390 a 1920px, la caja es siempre `100% x proporción de la foto` — 1.954
 * constante, sin alto fijo, sin recorte y sin un solo salto entre breakpoints.
 * Una caja de diseño congelada no puede dar eso: en cuanto su proporción difiere
 * de la de la foto, `cover` recorta en TODOS los anchos (en el banner de B2
 * Autos, 1.76 contra 1.96 = el 10% del ancho, justo donde van el texto y el
 * precio).
 *
 * Sólo cuando está solo en su sección: ahí crecer o achicarse no empuja a nadie
 * y la sección se mide por él.
 */
const FLUJO_MARCO_LIBRE = 'relative lg:ml-[var(--libre-x)] lg:mt-[var(--libre-y)] lg:w-[var(--libre-w)]';

/** La proporción de la caja tal como se eligió, o `null` si no se puede saber. */
const aspectoDe = (pos: PosicionLibre): number | null => {
  if (!pos.w || !pos.h) return null;
  const ancho = (ANCHO_DE_REFERENCIA * pos.w) / 100;
  const ar = ancho / pos.h;
  return Number.isFinite(ar) && ar > 0 ? ar : null;
};

/**
 * Ancho del canvas MÓVIL del editor, en px.
 *
 * Es el hermano de `ANCHO_DE_REFERENCIA`: cuando alguien arrastra la manija
 * estando en la vista móvil, la caja que ve mide esto de ancho, y contra eso
 * eligió el alto. Es el mismo 390 que usa `DevicePreviewFrame`.
 */
const ANCHO_DE_REFERENCIA_MOVIL = 390;

/**
 * Hasta dónde crece la composición móvil, en px.
 *
 * ## El problema que resuelve
 *
 * `m.x` y `m.w` van en PORCENTAJE del ancho, así que abajo de `lg` la
 * composición que alguien armó mirando un teléfono se estira sin freno hasta
 * 1279px. Medido en el hero de Concecars: la foto del auto pasa de 440×331 a
 * 1280px —donde manda escritorio— a **1062×626** un píxel más abajo. No es un
 * desborde ni un solape, no hay scroll horizontal y ninguna regla geométrica lo
 * ve: simplemente la foto mide dos veces y media lo que tiene que medir, y el
 * subtítulo queda en una sola línea de 1161px que nadie puede leer.
 *
 * ## Por qué 768 y no un número cualquiera
 *
 * Es el ancho del canvas de TABLET del editor (`ANCHOS_DISPOSITIVO.tablet`), o
 * sea el viewport más ancho para el que el editor todavía muestra —y deja
 * editar— este mismo layout. Arriba de ahí la vista de escritorio es la que
 * gobierna lo que la persona ve mientras diseña, así que estirar la composición
 * móvil más allá es dibujar algo que nadie compuso.
 *
 * ## Qué NO cambia
 *
 * Abajo de 768px, `min(100%, 768px)` es `100%` y toda la aritmética de
 * abajo se reduce, término por término, a lo que ya se emitía: `m.x%` y
 * `m.w%` del contenedor, con la sangría de centrado en cero. **Ningún teléfono
 * ni tablet angosta ve un píxel distinto.** Lo único que cambia es la franja de
 * 768 a 1279, que es justamente la que está rota.
 *
 * Y arriba de `lg` no participa: ahí mandan `--libre-x`/`--libre-y`, que no
 * se tocan. El diseño de escritorio queda idéntico.
 */
const TOPE_COMPOSICION_MOVIL = 768;

/**
 * El ancho EFECTIVO de la composición móvil, como expresión CSS.
 *
 * Es `%` y no `cqw` por una razón que costó una regresión: un porcentaje en
 * `left` y en `width` se resuelve contra el ANCHO DEL BLOQUE CONTENEDOR, que
 * es exactamente el mismo rectángulo contra el que ya resolvían `m.x` y
 * `m.w`. `100cqw`, en cambio, es la CAJA DE CONTENIDO del contenedor de
 * consulta más cercano: si ese elemento tiene relleno, los dos números dejan de
 * coincidir. Medido en el harness del área de piezas a 390px: la caja daba
 * 278,4px donde tenía que dar 310,4 —un 10% menos— y encima corrida.
 *
 * Con `%` no hace falta declarar ningún contenedor de consulta, no se arrastra
 * la contención de layout que `container-type` trae con él, y la cuenta usa el
 * mismo origen que antes. `top` y `height` no participan: se quedan en los
 * píxeles guardados.
 *
 * `--libre-tope` queda expuesta a propósito: es el único número ajustable de
 * todo esto y así se puede mover por sección el día que haga falta, sin volver
 * a tocar este archivo.
 *
 * MANTENER EN SYNC con el otro repo.
 */
export const ANCHO_COMPOSICION_MOVIL =
  `min(100%, var(--libre-tope, ${TOPE_COMPOSICION_MOVIL}px))`;

/**
 * Cuánto hay que correr la composición para que quede CENTRADA cuando el tope
 * ya actúa. Abajo del tope vale cero exacto y no mueve nada.
 *
 * Centrar y no dejarla pegada a la izquierda es lo que evita el otro defecto
 * que el tope podría introducir: una columna angosta arrinconada contra un
 * borde, con todo el aire de un lado.
 */
export const SANGRIA_COMPOSICION_MOVIL = `((100% - ${ANCHO_COMPOSICION_MOVIL}) / 2)`;

/**
 * La proporción de la caja MÓVIL, o `null` si no se puede saber.
 *
 * Hace falta porque `m.w` va en PORCENTAJE del ancho y `m.h` en PÍXELES: el
 * alto describe la caja únicamente en el ancho donde se eligió. Abajo de `xl`
 * el mismo par gobierna desde 320px hasta 1279px, así que la caja se deforma
 * sola a medida que la pantalla crece.
 *
 * Medido en el hero de Concecars (`m.w: 84`, `m.h: 193`): la caja pasaba de
 * 1.70 a 390px a 3.34 a 1279px, y como la foto se dibuja con `object-fit:
 * cover`, el recorte trepaba del 2% al 50%.
 */
export const aspectoMovilDe = (pos: PosicionLibre): number | null => {
  if (!pos.m?.w || !pos.m?.h) return null;
  const ancho = (ANCHO_DE_REFERENCIA_MOVIL * pos.m.w) / 100;
  const ar = ancho / pos.m.h;
  return Number.isFinite(ar) && ar > 0 ? ar : null;
};

/**
 * Cuánto ocupa de alto esa caja, en PORCENTAJE DEL ANCHO de su contenedor.
 *
 * Es lo que hay que reservar en el flujo para un marco que ahora se mide por
 * proporción: su alto ya no es un número de píxeles sino una fracción del
 * ancho, y un porcentaje de padding es lo único que en CSS se resuelve contra
 * el ancho. Cero cuando no hay proporción móvil que calcular.
 */
export const altoRelativoMovil = (pos: PosicionLibre): number => {
  const ar = aspectoMovilDe(pos);
  return ar && pos.m?.w ? pos.m.w / ar : 0;
};

/**
 * Alinear en vertical es repartir el sobrante del alto, asi que el envoltorio
 * pasa a ser una columna flex. Solo cuando hay `v`: sin eso el elemento sigue
 * siendo el bloque de siempre y nada de lo ya guardado cambia.
 */
const VERTICAL: Record<AlineacionVertical, string> = {
  start: 'flex flex-col justify-start',
  center: 'flex flex-col justify-center',
  end: 'flex flex-col justify-end',
};

/**
 * Las clases de abajo de `lg`, para cuando el elemento SÍ tiene posición móvil.
 *
 * Están escritas completas y separadas de las de escritorio a propósito: sin
 * `m` no se agrega ninguna, así que el HTML de todo lo ya guardado es el mismo
 * de antes carácter por carácter.
 */
const BASE_MOVIL = 'absolute left-[var(--libre-mx)] top-[var(--libre-my)] max-w-[calc(100%_-_var(--libre-mx))]';
const ANCHO_AUTO_MOVIL = 'w-fit';
const ANCHO_FIJO_MOVIL = 'w-[var(--libre-mw)]';
const ALTO_FIJO_MOVIL = 'h-[var(--libre-mh)]';
/**
 * Un marco con ancho Y alto móviles conserva su PROPORCIÓN abajo de `lg`, no
 * el alto en píxeles. `lg:aspect-auto` para que arriba del corte vuelva a
 * mandar `lg:h-[...]`, igual que hace `ASPECTO_MARCO` con escritorio.
 */
const ASPECTO_MARCO_MOVIL = 'aspect-[var(--libre-mar)] lg:aspect-auto';
/** Arriba de `xl` manda escritorio otra vez, incluso si móvil fijó medidas. */
const RESET_ANCHO = 'lg:w-auto';
const RESET_ALTO = 'lg:h-auto';

/**
 * Lo que recibe un marco abajo de `lg`: su proporción, o el alto si no hay.
 *
 * El que se mide solo recibe su ALTO de diseño, no una proporción. La
 * diferencia importa: con `scale-down` la imagen entra entera dentro de ese alto
 * —no se recorta ni se deforma— y las cuatro piezas de una tira quedan en cajas
 * de alto parecido (65/60/52/50 px en B2 Autos), que es lo que las hace ver
 * equilibradas. Una proporción impuesta, en cambio, volvía a cortar.
 */
/**
 * El alto de diseño como TOPE y nada más, para el marco que se mide solo.
 *
 * ## El blanco residual que saca
 *
 * El envoltorio le seguía imponiendo `aspect-[var(--libre-ar)]`, que es la
 * proporción de la caja que el dealer dibujó en ESCRITORIO. Pero un marco con
 * aspecto propio no la usa: la foto se dibuja con su proporción natural y el
 * carrusel con la de su espaciador. Las dos proporciones casi nunca coinciden,
 * y la diferencia queda como una franja en blanco DENTRO de la caja, abajo del
 * contenido. No se puede sacar achicando la sección, porque es la sección la
 * que se mide por ese envoltorio.
 *
 * Medido en el banner de la home de B2 Autos a 390px: la caja de escritorio da
 * 1.757 y la foto es 1.955, así que el envoltorio reservaba 222px para una foto
 * de 199 — 23px de blanco.
 *
 * Sin la proporción impuesta el envoltorio mide lo que mide su contenido, que
 * es exactamente lo que ya buscaba `tieneAspectoPropio`: la imagen se ve
 * ENTERA, sin recorte, sin deformar y sin franjas. El tope se queda, que es lo
 * que evita que una imagen grande se estire más allá del alto elegido.
 *
 * ## Por qué no colapsa a cero
 *
 * Era el riesgo del ticket #69 y ya está cubierto en el componente: el
 * `Carousel` renderiza su primera diapositiva como espaciador en el flujo
 * (`height: auto`, invisible) y las demás en absoluto encima. Medido con las
 * dos piezas: sin proporción impuesta, `Carousel` e `Image` dan el alto de su
 * foto, ninguno cero.
 *
 * MANTENER EN SYNC con goautos-admin.
 */
const TOPE_MARCO = 'max-h-[var(--libre-h)] lg:max-h-none';

const movilDeMarco = (pos: PosicionLibre, aspectoPropio = false): string =>
  aspectoPropio
    ? (pos.h ? TOPE_MARCO : '')
    : aspectoDe(pos) ? ASPECTO_MARCO : (pos.h ? ALTO_FIJO_MARCO : '');

/**
 * ¿Este marco se dibuja en el flujo arriba de `lg`? Ver `FLUJO_MARCO`.
 *
 * Pide proporción calculable y NINGUNA coordenada móvil propia: con `m` el
 * elemento ya es `absolute` abajo del corte y mezclar los dos modos no tiene
 * un caso real todavía. Sin eso, todo lo guardado se comporta igual que antes.
 */
const marcoEnFlujo = (pos: PosicionLibre, marco: boolean, soloEnSuSeccion: boolean): boolean =>
  marco && soloEnSuSeccion && !pos.m && !!aspectoDe(pos);

export const clasesDePosicion = (
  pos: PosicionLibre,
  marco = false,
  /** El marco es el único hijo de su `Section` en modo libre. */
  soloEnSuSeccion = false,
  /** El marco trae su propia proporción y abajo de `lg` se mide solo. */
  aspectoPropio = false,
): string => {
  // Abajo de `xl` esto emite exactamente las mismas clases que la rama de
  // siempre (`relative` + la proporción): lo único que cambia es arriba.
  if (marcoEnFlujo(pos, marco, soloEnSuSeccion)) {
    return [aspectoPropio ? FLUJO_MARCO_LIBRE : FLUJO_MARCO, filaDe(pos, marco)].join(' ');
  }

  const escritorio = [
    pos.w ? ANCHO_FIJO : ANCHO_AUTO,
    pos.h ? ALTO_FIJO : '',
    pos.v ? VERTICAL[pos.v] : '',
  ];
  // Sin posición móvil: exactamente lo de siempre, `relative` y a apilarse. La
  // única diferencia es el marco, que abajo de `lg` ya no puede quedarse sin
  // ninguna medida.
  if (!pos.m) {
    return [BASE, filaDe(pos, marco), marco ? movilDeMarco(pos, aspectoPropio) : '', ...escritorio]
      .filter(Boolean).join(' ');
  }

  // Un marco con las dos medidas móviles se mide por proporción y no por el
  // alto congelado: ver `aspectoMovilDe`. Sólo para marcos, a propósito — a un
  // texto una proporción fija lo desbordaría en cuanto necesitara una línea
  // más.
  const aspectoMovil = marco ? aspectoMovilDe(pos) : null;

  return [
    // `BASE` sin el `relative`: en móvil el elemento también va absoluto.
    BASE.replace('relative ', ''),
    BASE_MOVIL,
    pos.m.w ? ANCHO_FIJO_MOVIL : ANCHO_AUTO_MOVIL,
    aspectoMovil ? ASPECTO_MARCO_MOVIL : (pos.m.h ? ALTO_FIJO_MOVIL : ''),
    // Con alto móvil propio manda ése; si no lo hay, el marco conserva la
    // proporción de escritorio en vez de quedarse sin ninguna medida.
    marco && !pos.m.h ? movilDeMarco(pos, aspectoPropio) : '',
    // Si móvil fija medidas y escritorio no, hay que devolverlas a auto arriba
    // de `xl`; si escritorio las fija, sus propias clases ganan por orden.
    pos.m.w && !pos.w ? RESET_ANCHO : '',
    pos.m.h && !pos.h && !aspectoMovil ? RESET_ALTO : '',
    pos.m.v ? VERTICAL[pos.m.v] : '',
    ...escritorio,
  ].filter(Boolean).join(' ');
};

export const variablesDePosicion = (pos: PosicionLibre): React.CSSProperties =>
  ({
    ['--libre-x']: `${pos.x}%`,
    ['--libre-y']: `${pos.y}px`,
    ...(pos.w ? { ['--libre-w']: `${pos.w}%` } : {}),
    ...(pos.h ? { ['--libre-h']: `${pos.h}px` } : {}),
    // La proporción de la caja, para que el marco la conserve en móvil. Se
    // emite siempre que se pueda calcular: la usa sólo quien la necesita.
    ...(aspectoDe(pos) ? { ['--libre-ar']: `${aspectoDe(pos)}` } : {}),
    ...(pos.m ? {
      // Horizontal contra el ancho EFECTIVO (ver `ANCHO_COMPOSICION_MOVIL`), no
      // contra el bloque contenedor entero: es lo que le pone un techo al crecimiento.
      // Abajo de 768px estas dos expresiones valen exactamente `m.x%` y
      // `m.w%`, que es lo que se emitía antes.
      ['--libre-mx']:
        `calc(${SANGRIA_COMPOSICION_MOVIL} + ${pos.m.x} * ${ANCHO_COMPOSICION_MOVIL} / 100)`,
      // La vertical se deja en los píxeles guardados, a propósito. Escalarla
      // junto con el ancho estiraría el alto de una caja de texto hasta el
      // doble sin que la letra crezca, y lo que quedaría adentro es el hueco
      // que este cambio viene a eliminar. El alto de un MARCO sí acompaña,
      // pero por su proporción (`--libre-mar`), que ya se mide contra el ancho.
      ['--libre-my']: `${pos.m.y}px`,
      ...(pos.m.w ? {
        ['--libre-mw']: `calc(${pos.m.w} * ${ANCHO_COMPOSICION_MOVIL} / 100)`,
      } : {}),
      ...(pos.m.h ? { ['--libre-mh']: `${pos.m.h}px` } : {}),
      // La proporción de la caja móvil, para el marco que se mide por ella.
      // Se emite siempre que se pueda calcular: la usa sólo quien la necesita.
      ...(aspectoMovilDe(pos) ? { ['--libre-mar']: `${aspectoMovilDe(pos)}` } : {}),
    } : {}),
  } as React.CSSProperties);

/**
 * Envoltorio del elemento posicionado.
 *
 * Es la ÚNICA pieza que sabe de posición y tamaño, y la usan los dos repos: el
 * editor la mete desde `onRender` y el sitio también. Así ningún componente
 * necesita saber nada del modo libre, y agregar uno nuevo a la lista no toca su
 * código.
 */
export const EnvoltorioLibre: React.FC<{
  posicion: PosicionLibre | null;
  /**
   * Nombre del componente envuelto. Sólo se usa para saber si es un marco y
   * darle alto también abajo de `lg`. Ausente = se comporta como siempre, que
   * es lo que mantiene intacto a quien todavía no lo pasa.
   */
  nombre?: string;
  /**
   * El marco es el único hijo de su `Section` en modo libre y entonces arriba
   * de `xl` se queda en el flujo conservando su proporción. Ver `FLUJO_MARCO`.
   * Ausente = el comportamiento de siempre.
   */
  soloEnSuSeccion?: boolean;
  children?: React.ReactNode;
}> = ({ posicion, nombre, soloEnSuSeccion = false, children }) => (
  // Sin posición el envoltorio igual se dibuja, solo que `relative` y sin
  // medidas: el elemento sigue en el flujo. Hace falta para que el editor tenga
  // dónde anclar los controles del elemento que todavía nadie movió, y para que
  // el DOM del sitio sea idéntico al del editor en ese estado.
  <div
    data-libre=""
    className={
      posicion
        ? clasesDePosicion(posicion, esMarco(nombre), soloEnSuSeccion, tieneAspectoPropio(nombre))
        : 'relative'
    }
    // La capa va con `pointer-events: none` para no tapar la sección; sus hijos
    // los vuelven a habilitar.
    style={{ pointerEvents: 'auto', ...(posicion ? variablesDePosicion(posicion) : {}) }}
  >
    {children}
  </div>
);

/**
 * Alto que necesita el área para contener a los hijos posicionados.
 *
 * Un hijo en posición libre es `absolute`: NO empuja el alto de la sección. Sin
 * esto, apenas se mueven un par de elementos el área se achica a su altura
 * mínima, los elementos quedan visualmente afuera y —lo peor— soltar el
 * siguiente componente ahí lo deja como hermano de la sección en vez de adentro.
 *
 * Se calcula con los props (`y + h`) y no midiendo el DOM a propósito: medir
 * durante un arrastre realimenta el layout y hace temblar el elemento.
 */
export const useAltoDeHijosLibres = (id: string, libre: boolean): number => {
  const { fondos } = useEditor((state) => ({
    fondos: (state.nodes[id]?.data?.nodes ?? []).map((hijo) => {
      const pos = normalizarPosicion((state.nodes[hijo]?.data?.props as Record<string, unknown>)?.[PROP_POSICION]);
      return pos ? pos.y + (pos.h ?? 0) : 0;
    }),
  }));
  if (!libre || !fondos.length) return 0;
  // Un respiro abajo del último elemento, para poder soltar algo nuevo ahí.
  return Math.max(0, ...fondos) + 24;
};

/**
 * Lo mismo, pero para la composición MÓVIL: hasta dónde llega la pieza más baja
 * según su `m`, en px.
 *
 * ## Por qué hace falta un cálculo aparte
 *
 * `useAltoDeHijosLibres` suma `y + h`, que son las coordenadas de ESCRITORIO.
 * Abajo de `lg` ésas no se aplican —ahí gobiernan las de `m`—, así que el
 * número que devuelve no describe nada de lo que se ve en el teléfono.
 *
 * Mientras las piezas estaban en el flujo eso no importaba: su propio alto
 * empujaba el de la sección. Desde que el Builder congela la composición móvil
 * de la sección entera en el primer arrastre, ya no queda nadie en el flujo, el
 * `min-height` de la sección arranca recién en `lg` (`MIN_H_SECCION_LIBRE`) y
 * sin esto la sección se serviría colapsada, con sus piezas dibujadas por fuera
 * y la sección siguiente encima.
 *
 * Es el hermano de `useAltoMovilDePiezas`, que ya resuelve lo mismo para las
 * piezas internas de una sección tradicional.
 *
 * Cero cuando ninguna pieza tiene `m`: ahí siguen todas en el flujo, que se
 * mide solo, y no se emite ninguna declaración nueva — una página guardada
 * antes de que esto existiera sale con el mismo HTML, carácter por carácter.
 *
 * MANTENER EN SYNC con goautos-admin.
 */
export const useAltoMovilDeHijosLibres = (id: string, libre: boolean): number => {
  const { piezas } = useEditor((state) => ({
    piezas: (state.nodes[id]?.data?.nodes ?? []).map((hijo) => {
      const m = normalizarPosicion(
        (state.nodes[hijo]?.data?.props as Record<string, unknown>)?.[PROP_POSICION],
      )?.m;
      return { id: hijo, y: m?.y ?? 0, h: m?.h ?? 0, tieneM: !!m };
    }),
  }));

  /**
   * El alto REAL de las piezas que tienen `m.y` pero no `m.h`.
   *
   * `m.h` es opcional y sólo lo escribe un gesto de redimensionado, así que una
   * pieza que únicamente se movió no lo tiene. Dando por supuesto cero, la
   * reserva se quedaba corta justo lo que la pieza mide: medido en la home de
   * B2 Autos, el divisor tiene `m.y: 331` y mide 43px, así que llegaba hasta
   * 374 mientras la sección reservaba hasta 331 — 19px de la pieza dibujados
   * fuera de su propia sección.
   *
   * ## Por qué no hay ciclo con el `min-height` de la sección
   *
   * Lo que se mide es una pieza `absolute` con `top` fijo y sin alto impuesto:
   * su caja la define su contenido, no su contenedor, así que el número no
   * depende del alto de la sección y volver a medir da lo mismo. La guarda de
   * abajo cubre el caso contrario —una pieza que SÍ se estire a su contenedor,
   * como un marco con `height: 100%`—: ahí el alto medido sería el de la
   * sección y realimentaría el mínimo, así que se descarta y se vuelve al
   * comportamiento anterior.
   *
   * El estado sólo se escribe cuando el número cambia de verdad, que es lo que
   * evita el bucle de renders.
   */
  const porMedir = piezas.filter((p) => p.tieneM && !p.h).map((p) => p.id);
  const { query } = useEditor();
  const [medidos, setMedidos] = React.useState<Record<string, number>>({});
  const clave = porMedir.join('|');

  React.useEffect(() => {
    if (!libre || !porMedir.length) return;
    const nodos = porMedir
      .map((idPieza) => {
        try {
          const dom = query.node(idPieza).get().dom as HTMLElement | null;
          return dom ? { id: idPieza, el: (dom.closest('[data-libre]') as HTMLElement | null) ?? dom } : null;
        } catch {
          return null;
        }
      })
      .filter(Boolean) as Array<{ id: string; el: HTMLElement }>;
    if (!nodos.length) return;

    const medir = () => {
      const siguiente: Record<string, number> = {};
      for (const { id: idPieza, el } of nodos) {
        if (!el.isConnected) continue;
        const alto = Math.round(el.getBoundingClientRect().height);
        // La guarda: una pieza que se estira a su contenedor mide lo mismo que
        // él, y usar ese número realimentaría el mínimo de la sección.
        const contenedor = el.offsetParent as HTMLElement | null;
        const altoContenedor = contenedor ? Math.round(contenedor.getBoundingClientRect().height) : 0;
        if (!alto || (altoContenedor && alto >= altoContenedor)) continue;
        siguiente[idPieza] = alto;
      }
      setMedidos((previo) => {
        const igual = Object.keys(siguiente).length === Object.keys(previo).length
          && Object.entries(siguiente).every(([k, v]) => previo[k] === v);
        return igual ? previo : siguiente;
      });
    };

    medir();
    const observador = new ResizeObserver(medir);
    nodos.forEach(({ el }) => observador.observe(el));
    return () => observador.disconnect();
    // `clave` representa qué piezas hay que medir; `piezas` cambia de identidad
    // en cada render y volvería a enganchar el observador sin necesidad.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave, libre, query]);

  if (!libre || !piezas.length) return 0;
  const fondos = piezas.map((p) => (p.tieneM ? p.y + (p.h || medidos[p.id] || 0) : 0));
  return Math.max(0, ...fondos);
};

/**
 * Estado libre del nodo actual: si su padre es una sección en modo libre, si el
 * componente está habilitado, y qué posición tiene guardada.
 *
 * Se mira el prop del PADRE en vez de marcar cada hijo: así cambiar el modo de
 * la sección alcanza para que todos sus hijos cambien de comportamiento, y no
 * queda estado duplicado que se pueda desincronizar.
 */
export const useNodoLibre = () => {
  const { parentId, nombre, posicionCruda } = useNode((node) => ({
    parentId: node.data.parent,
    nombre: node.data.name,
    posicionCruda: (node.data.props as Record<string, unknown>)?.[PROP_POSICION],
  }));
  const { padreLibre, soloEnSuSeccion } = useEditor((state) => {
    const padre = parentId ? state.nodes[parentId]?.data : undefined;
    const props = padre?.props as Record<string, unknown> | undefined;
    return {
      // Único hijo de una `Section` en modo libre. La capa de una sección
      // existente NO cuenta: ahí el alto lo pone la sección de abajo y crecer
      // en el flujo de la capa no la movería. Ver `FLUJO_MARCO`.
      soloEnSuSeccion: padre?.name === 'Section'
        && props?.layoutMode === 'free'
        && (padre?.nodes?.length ?? 0) === 1,
      // Dos padres habilitan el movimiento libre: una `Section` puesta en modo
      // libre, y la capa que se le cuelga a una sección existente.
      padreLibre: (padre?.props as Record<string, unknown> | undefined)?.layoutMode === 'free'
        || (padre?.custom as Record<string, unknown> | undefined)?.capaLibre === true,
    };
  });

  const posicion = normalizarPosicion(posicionCruda);
  return {
    soloEnSuSeccion,
    // `posicion` ausente = el elemento sigue en el flujo aunque la sección esté
    // en modo libre. Por eso pasar una sección a libre no mueve nada.
    libre: !!padreLibre && esMovible(nombre) && !!posicion,
    habilitado: !!padreLibre && esMovible(nombre),
    posicion,
    nombre,
    politica: politicaDe(nombre),
  };
};
